/* «Улучшить» в полях описания (engine-kit, план M3 §2 S5, D3): ядро без React, общее для родной кнопки (photo/PhotoImprove.tsx — теперь
   обёртка над сессией) и движков шаблона (window.BotText.improveInline, который зовёт BotCore.improveBtn, legacy/bot-core.js:3888).
   Виды photo · video · mcard · post · music — POST /app/studio/<kind>/improve (MessagingPhotoPromptsController, MessagingVideoPromptsController,
   MessagingStudioTextToolsController.cs:32,47), бесплатно и без заказа:
   - ключ идемпотентности (UUID) и текст ложатся в sessionStorage `steos:<kind>:improve:<owner>` до POST; хранилище недоступно — POST нет;
     чужое или повреждённое намерение не восстанавливается и блокирует новую отправку;
   - ответ pending (202) — ACK, а не готовность: дальше только GET того же ключа раз в 2 с, не дольше 130 с; повторного POST нет;
   - неизвестный исход (потерянный ответ) — намерение стоит, «Проверить улучшение» читает тот же ключ; GET 404 в течение суток разрешает
     «Повторить прежнее улучшение» (POST тем же ключом и тем же текстом); срок проверки — сутки;
   - первый определённый 422 `<kind>_improvement_input_invalid` освобождает намерение («Измените описание…»);
   - приёмка ответа: тот же request_id, известный статус, бесплатно (chargeable false, amount 0), до трёх вариантов не длиннее 500 знаков.
   Вид text (и seo) — POST /app/text/improve (03-api §7.7), только если экран публикует improve_available (setTextImprove), как родной
   TextImprove. improveInline никогда не бросает: варианты или [] (BotCore покажет «Не получилось улучшить…»; Q-PV7 — прежнее улучшение
   другого текста ещё проверяется — тоже []), поэтому демо-варианты BotCore (demoVars) в серверном режиме недостижимы. */
import { api, endpoints } from '../../../api';
import { ApiError, uuid } from '../../../api/client';
import { PROMPT_MAX } from '../prompt-limit';

export type ImproveKind = 'photo' | 'video' | 'mcard' | 'post' | 'music';
const KINDS: readonly string[] = ['photo', 'video', 'mcard', 'post', 'music'];
export const isImproveKind = (value: unknown): value is ImproveKind => typeof value === 'string' && KINDS.includes(value);

export interface Improved {
  request_id: string;
  status: 'done' | 'pending' | 'uncertain' | 'failed' | 'rejected';
  variants: string[];
  amount: number;
  chargeable: boolean;
}
export interface ImproveIntent {
  owner: number;
  key: string;
  text: string;
  createdAt: number;
  result?: Improved;
}
export interface ImproveState {
  owner?: number;
  intent: ImproveIntent | null;
  busy: boolean;
  error: string;
  replayAllowed: boolean;
  blocked: boolean;
}

/** Срок, в который неподтверждённое улучшение ещё проверяется и повторяется тем же ключом */
export const IMPROVE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_TEXT = PROMPT_MAX;
export const improveKey = (owner: number, kind: ImproveKind) => `steos:${kind}:improve:${owner}`;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FINAL = ['done', 'failed', 'rejected'];
/** Намерение ещё не дошло до итога (нет ответа, pending, uncertain) */
export const improvePending = (intent: ImproveIntent | null | undefined) => !!intent && !FINAL.includes(intent.result?.status ?? '');
export const improveExpired = (intent: ImproveIntent | null | undefined, now = Date.now()) => !!intent?.createdAt && now - intent.createdAt >= IMPROVE_TTL_MS;

export function validImproved(value: Improved, key: string): boolean {
  return (
    !!value &&
    value.request_id === key &&
    ['done', 'pending', 'uncertain', 'failed', 'rejected'].includes(value.status) &&
    value.chargeable === false &&
    value.amount === 0 &&
    Array.isArray(value.variants) &&
    value.variants.length <= 3 &&
    (value.status !== 'done' || value.variants.length > 0) &&
    value.variants.every(text => typeof text === 'string' && Array.from(text).length <= MAX_TEXT)
  );
}
/** Сохранённое намерение владельца этого вида: нет — null; чужое, битое или с повреждённым ответом — исключение */
function restoreIntent(owner: number, kind: ImproveKind): ImproveIntent | null {
  const raw = sessionStorage.getItem(improveKey(owner, kind));
  if (!raw) return null;
  const stored = JSON.parse(raw) as ImproveIntent;
  if (
    stored.owner !== owner ||
    !UUID.test(stored.key) ||
    typeof stored.text !== 'string' ||
    Array.from(stored.text).length > MAX_TEXT ||
    !Number.isFinite(stored.createdAt) ||
    stored.createdAt <= 0 ||
    stored.createdAt > Date.now() + 60000 ||
    (stored.result !== undefined && !validImproved(stored.result, stored.key))
  )
    throw new Error('invalid');
  return stored;
}

export interface ImproveSession {
  get(): ImproveState;
  /** Слушатель получает новое состояние после каждого изменения */
  subscribe(listener: (state: ImproveState) => void): () => void;
  /** Монтирование: владелец (GET /app/me) и сохранённое намерение этого вида; промис — когда они известны */
  start(): Promise<void>;
  /** «Улучшить» (readOnly false: POST; неготовое прежнее намерение — его ключ и текст) или «Проверить улучшение» (readOnly: GET того же ключа) */
  execute(readOnly: boolean, text: string): Promise<void>;
  /** «Начать новое улучшение» (срок проверки истёк): намерение снимается */
  reset(): void;
  /** Размонтирование: идущий запрос прерывается, ответы ничего не меняют */
  stop(): void;
}

/** Сессия «Улучшить» одного вида (одна кнопка, один экран) */
export function createImproveSession(kind: ImproveKind): ImproveSession {
  let state: ImproveState = { owner: undefined, intent: null, busy: false, error: '', replayAllowed: false, blocked: false };
  const listeners = new Set<(state: ImproveState) => void>();
  const set = (patch: Partial<ImproveState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener(state));
  };
  let active = true,
    flight = false,
    request: AbortController | null = null;

  function start(): Promise<void> {
    active = true;
    return endpoints
      .me()
      .then(me => {
        if (!active) return;
        set({ owner: me.user.id });
        try {
          const stored = restoreIntent(me.user.id, kind);
          if (stored) set({ intent: stored });
        } catch {
          set({ blocked: true, error: 'Не удалось восстановить улучшение. Новая отправка заблокирована.' });
        }
      })
      .catch(() => {
        if (active) set({ error: 'Не удалось проверить владельца.' });
      });
  }

  async function execute(readOnly: boolean, text: string) {
    const { owner, blocked, intent } = state;
    if (!owner || flight || blocked) return;
    if (!readOnly && (!text.trim() || Array.from(text).length > MAX_TEXT)) return;
    const storage = improveKey(owner, kind);
    flight = true;
    set({ busy: true, error: '' });
    const controller = new AbortController();
    request = controller;
    let timeout = setTimeout(() => controller.abort(), readOnly ? 30000 : 125000);
    const cancelled = new Promise<never>((_, reject) =>
      controller.signal.addEventListener('abort', () => reject(new Error('cancelled')), { once: true }),
    );
    const previous = intent && improvePending(intent) ? intent : null;
    const current: ImproveIntent = previous ?? { owner, key: uuid(), text, createdAt: Date.now() };
    let persisted = !!previous;
    let initialRequest = true;
    let pause: ReturnType<typeof setTimeout> | undefined;
    try {
      if ((await Promise.race([endpoints.me(), cancelled])).user.id !== owner) throw new Error('owner_changed');
      sessionStorage.setItem(storage, JSON.stringify(current));
      persisted = true;
      set({ intent: current });
      const operation = readOnly
        ? api.request<Improved>(`/app/studio/${kind}/improve/${current.key}`, { signal: controller.signal })
        : api.request<Improved>(`/app/studio/${kind}/improve`, {
            method: 'POST',
            body: { text: current.text },
            idempotency: current.key,
            signal: controller.signal,
          });
      let result = await Promise.race([operation, cancelled]);
      initialRequest = false;
      if (controller.signal.aborted || !active) return;
      if (!validImproved(result, current.key)) throw new Error('improve_invalid');
      const saveResult = (value: Improved) => {
        if (!validImproved(value, current.key)) throw new Error('improve_invalid');
        const next = { ...current, result: value };
        sessionStorage.setItem(storage, JSON.stringify(next));
        set({ intent: next, replayAllowed: false });
      };
      saveResult(result);
      if (result.status === 'pending') {
        // ACK не означает готовность. После него читаем только прежний UUID, без повторного POST.
        clearTimeout(timeout);
        timeout = setTimeout(() => controller.abort(), 130000);
        while (result.status === 'pending' && active && !controller.signal.aborted) {
          await Promise.race([
            new Promise<void>(resolve => {
              pause = setTimeout(resolve, 2000);
            }),
            cancelled,
          ]);
          clearTimeout(pause);
          result = await Promise.race([api.request<Improved>(`/app/studio/${kind}/improve/${current.key}`, { signal: controller.signal }), cancelled]);
          if (controller.signal.aborted || !active) return;
          saveResult(result);
        }
      }
    } catch (failure) {
      if (!persisted && active) set({ blocked: true });
      if (
        initialRequest &&
        !previous &&
        !readOnly &&
        failure instanceof ApiError &&
        failure.status === 422 &&
        failure.code === `${kind}_improvement_input_invalid`
      ) {
        try {
          const stored = JSON.parse(sessionStorage.getItem(storage) ?? 'null') as ImproveIntent | null;
          if (stored?.key === current.key) sessionStorage.removeItem(storage);
        } catch {
          if (active) set({ blocked: true, error: 'Не удалось восстановить улучшение. Новая отправка заблокирована.' });
          return;
        }
        if (active) set({ intent: null, error: 'Измените описание и попробуйте улучшить его снова.' });
        return;
      }
      if (readOnly && failure instanceof ApiError && failure.status === 404 && current.createdAt && Date.now() - current.createdAt < IMPROVE_TTL_MS)
        if (active) set({ replayAllowed: true });
      if (active) set({ error: 'Улучшение пока не подтверждено. Исходный текст сохранён.' });
    } finally {
      clearTimeout(timeout);
      clearTimeout(pause);
      flight = false;
      if (active) set({ busy: false });
    }
  }

  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    execute,
    reset() {
      if (state.owner) sessionStorage.removeItem(improveKey(state.owner, kind));
      set({ intent: null, error: '', replayAllowed: false });
    },
    stop() {
      active = false;
      request?.abort();
    },
  };
}

/* ---------- движок шаблона: BotText.improveInline ---------- */

/** Итог одного «Улучшить» движка: намерение с ответом сервера (или null — ничего не вышло) */
async function ask(kind: ImproveKind, text: string): Promise<ImproveIntent | null> {
  const session = createImproveSession(kind);
  await session.start();
  try {
    let now = session.get();
    if (!now.owner || now.blocked) return null;
    const old = now.intent;
    if (old && improvePending(old) && !improveExpired(old)) {
      /* прежнее улучшение не дошло до итога: сначала только GET того же ключа */
      await session.execute(true, text);
      now = session.get();
      const checked = now.intent;
      if (checked && improvePending(checked)) {
        /* ещё проверяется (другой текст — Q-PV7) или исход неизвестен; 404 в течение суток у того же текста — повтор тем же ключом */
        if (checked.text !== text || !now.replayAllowed) return null;
        await session.execute(false, text);
        return session.get().intent;
      }
      if (checked?.text === text) return checked;
    } else if (old && improvePending(old)) session.reset();
    await session.execute(false, text);
    return session.get().intent;
  } finally {
    session.stop();
  }
}

let textAvailable: (() => boolean) | null = null;
/** Экран вида text (кавер, «Текст») публикует improve_available: «Улучшить» вида text идёт в POST /app/text/improve; null — недоступно */
export function setTextImprove(available: (() => boolean) | null) {
  textAvailable = available;
}
/** Варианты POST /app/text/improve, как родной TextImprove: строки, непустые, не длиннее max, до трёх */
async function textVariants(text: string, max: number): Promise<string[]> {
  if (!textAvailable?.()) return [];
  const result = await api.request<{ variants?: unknown }>('/app/text/improve', { method: 'POST', body: { kind: 'text', text, max } });
  return Array.isArray(result.variants)
    ? result.variants.filter((item): item is string => typeof item === 'string' && !!item.trim() && Array.from(item).length <= max).slice(0, 3)
    : [];
}

/** window.BotText.improveInline(текст, {kind, max} | kind) для BotCore.improveBtn: до трёх вариантов не длиннее max. Никогда не бросает:
    сбой, отказ, неподтверждённый исход, прежнее улучшение другого текста ещё проверяется, вид без маршрута — [] */
export async function improveInline(text: unknown, o?: { kind?: unknown; max?: unknown } | string): Promise<string[]> {
  try {
    const opts = typeof o === 'string' ? { kind: o } : (o ?? {});
    const source = typeof text === 'string' ? text : '';
    const max = typeof opts.max === 'number' && opts.max > 0 ? opts.max : Infinity;
    if (!source.trim()) return [];
    if (opts.kind === 'text' || opts.kind === 'seo') return await textVariants(source, max);
    if (!isImproveKind(opts.kind)) return [];
    const intent = await ask(opts.kind, source);
    if (!intent || intent.text !== source || intent.result?.status !== 'done') return [];
    return intent.result.variants.filter(item => !!item.trim() && Array.from(item).length <= max).slice(0, 3);
  } catch {
    return [];
  }
}
